import { request } from "@utils/request"

import type { ErrorData, ErrorLoggerConfig } from "./types"

export const FLUSH_THRESHOLD = 200
export const MAX_QUEUE_SIZE = 1000
export const RETRY_COUNT = 3
export const MAX_ITEMS_PER_REQUEST = 200
export const KEEPALIVE_MAX_BODY_BYTES = 60_000

type IngestResponse = {
  success: boolean
  processed: number
}

export type FlushOptions = {
  keepalive?: boolean
}

export type BatchFlusher<T> = {
  enqueue: (item: T) => { shouldFlush: boolean }
  flush: (options?: FlushOptions) => Promise<void>
  size: () => number
}

export type BatchFlusherConfig<T> = {
  endpoint: string
  buildBody: (items: T[]) => unknown
  flushThreshold?: number
  maxQueueSize?: number
  maxKeepaliveBodyBytes?: number
}

const byteLength = (body: unknown): number =>
  new TextEncoder().encode(JSON.stringify(body)).length

const countFittingItems = <T>(
  items: T[],
  buildBody: (items: T[]) => unknown,
  maxBytes: number,
): number => {
  if (byteLength(buildBody(items)) <= maxBytes) return items.length
  let low = 0
  let high = items.length - 1
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    if (byteLength(buildBody(items.slice(0, mid))) <= maxBytes) low = mid
    else high = mid - 1
  }
  return low
}

export const createBatchFlusher = <T>(
  config: BatchFlusherConfig<T>,
): BatchFlusher<T> => {
  const flushThreshold = config.flushThreshold ?? FLUSH_THRESHOLD
  const maxQueueSize = config.maxQueueSize ?? MAX_QUEUE_SIZE
  const maxKeepaliveBodyBytes =
    config.maxKeepaliveBodyBytes ?? KEEPALIVE_MAX_BODY_BYTES
  let queue: T[] = []

  const enqueue = (item: T) => {
    queue.push(item)
    if (queue.length > maxQueueSize) {
      queue = queue.slice(-maxQueueSize)
    }
    return { shouldFlush: queue.length >= flushThreshold }
  }

  const flush = async (options?: FlushOptions): Promise<void> => {
    try {
      if (queue.length === 0) return
      if (!navigator.onLine) return

      const candidates = queue.slice(0, MAX_ITEMS_PER_REQUEST)
      const count = options?.keepalive
        ? countFittingItems(candidates, config.buildBody, maxKeepaliveBodyBytes)
        : candidates.length
      if (count === 0) return

      const requestBody = config.buildBody(candidates.slice(0, count))
      queue = queue.slice(count)
      await request
        .post(config.endpoint, {
          prefix: "",
          credentials: "include",
          json: requestBody,
          retry: { limit: RETRY_COUNT, methods: ["post"] },
          keepalive: options?.keepalive ?? false,
        })
        .json<IngestResponse>()
    } catch (error) {
      // oxlint-disable-next-line no-console -- Last resort, must not recurse
      console.warn("Failed to send logs to error service after retries:", error)
    }
    if (!options?.keepalive) await flush(options)
  }

  return { enqueue, flush, size: () => queue.length }
}

export type LogFlusher = BatchFlusher<ErrorData>

export const createLogFlusher = (config: ErrorLoggerConfig): LogFlusher =>
  createBatchFlusher<ErrorData>({
    endpoint: `${config.baseUrl.replace(/\/$/u, "")}/ingest`,
    buildBody: (errors) => ({
      errors: errors.map((errorData) => ({
        timestamp: new Date(errorData.timestamp),
        level: "error" as const,
        message: errorData.message,
        context: {
          url: errorData.url,
          userAgent: errorData.userAgent,
          stack: errorData.stack,
          componentStack: errorData.componentStack,
          appName: config.appName,
          appVersion: config.appVersion,
        },
      })),
    }),
  })

export type Debounced = (() => void) & { cancel: () => void }

export const debounce = (
  fn: () => void,
  opts: { wait: number; maxWait: number },
): Debounced => {
  let timer: ReturnType<typeof setTimeout> | undefined = undefined
  let firstCallAt: number | undefined = undefined

  const cancel = () => {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
    firstCallAt = undefined
  }

  const run = () => {
    cancel()
    fn()
  }

  const debounced = () => {
    const now = Date.now()
    firstCallAt ??= now
    if (timer !== undefined) clearTimeout(timer)
    const remainingMaxWait = opts.maxWait - (now - firstCallAt)
    timer = setTimeout(run, Math.max(0, Math.min(opts.wait, remainingMaxWait)))
  }

  return Object.assign(debounced, { cancel })
}
